/**
 * dashboardGridConfig.js - Grid math and react-grid-layout config shared
 * between the editable "My Dashboard" grid and the read-only preset
 * dashboard (template) grid, both rendered from src/pages/Dashboard.js.
 */

export const GRID_COLS = 24;
export const GRID_ROW_HEIGHT = 150;
export const GRID_MARGIN = 8;
export const GRID_CONTAINER_PADDING = 8;

/**
 * GridLayout props identical across the editable and read-only grids.
 * Each grid instance spreads this and sets layout/width/isDraggable/
 * isResizable/callbacks/resizeHandles/style individually, since those
 * genuinely differ between the two.
 */
export const DASHBOARD_GRID_STATIC_PROPS = {
  cols: GRID_COLS,
  rowHeight: GRID_ROW_HEIGHT,
  margin: [GRID_MARGIN, GRID_MARGIN],
  containerPadding: [GRID_CONTAINER_PADDING, GRID_CONTAINER_PADDING],
  compactType: null,
  preventCollision: true,
  autoSize: false,
  allowOverlap: false,
  useCSSTransforms: false
};

/**
 * Estimates a chart tile's actual rendered pixel size from its grid units
 * and buckets it into a watermark density tier, so the chart service can
 * scale down watermark density (fewer/lighter marks) for small tiles
 * instead of always rendering full density regardless of how small the
 * tile is.
 *
 * Mirrors the pixel math react-grid-layout itself uses for the grid
 * configured above (cols=24, rowHeight=150, margin=[8,8],
 * containerPadding=[8,8]) - approximate is fine, this only needs to
 * pick the right bucket, not be pixel-perfect.
 *
 * @param {number} w - Grid column units for the chart's current layout item
 * @param {number} h - Grid row units for the chart's current layout item
 * @param {number} containerWidth - Current grid container width in px (windowWidth)
 * @returns {'xs'|'sm'|'md'|'lg'}
 */
export const getWatermarkSizeTier = (w, h, containerWidth) => {
  const colWidth = (containerWidth - GRID_MARGIN * (GRID_COLS - 1) - GRID_CONTAINER_PADDING * 2) / GRID_COLS;
  const pixelWidth = w * colWidth + GRID_MARGIN * (w - 1);
  const pixelHeight = h * GRID_ROW_HEIGHT + GRID_MARGIN * (h - 1);
  const effectiveSize = Math.min(pixelWidth, pixelHeight);

  if (effectiveSize < 320) return 'xs';
  if (effectiveSize < 550) return 'sm';
  if (effectiveSize < 900) return 'md';
  return 'lg';
};
